import { useCallback, useRef, useState } from "react";
import PathBomInsufficientStockDialog from "../components/PathBomInsufficientStockDialog";
import PathBomStockReviewDialog from "../components/PathBomStockReviewDialog";
import {
  checkPathBomStockForLabTests,
  deductPathInventoryForLabTests,
} from "../services/pathBomInventoryGate";

/**
 * Shared collect-time inventory UX:
 * 1) Check BOM stock
 * 2) Insufficient → error dialog
 * 3) Sufficient → review dialog (qty / batch / expiry); on confirm → deduct
 * 4) No BOM → proceed without dialog/deduct
 */
export function usePathBomCollectInventoryFlow() {
  const [insufficientOpen, setInsufficientOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [stockChecks, setStockChecks] = useState([]);
  const [reviewConfirming, setReviewConfirming] = useState(false);
  const reviewResolverRef = useRef(null);
  const pendingDeductRef = useRef(null);

  const closeInsufficient = useCallback(() => {
    setInsufficientOpen(false);
  }, []);

  const cancelReview = useCallback(() => {
    if (reviewConfirming) return;
    setReviewOpen(false);
    pendingDeductRef.current = null;
    const resolve = reviewResolverRef.current;
    reviewResolverRef.current = null;
    if (resolve) resolve({ ok: false, reason: "cancelled" });
  }, [reviewConfirming]);

  const confirmReview = useCallback(async () => {
    const pending = pendingDeductRef.current;
    const resolve = reviewResolverRef.current;
    if (!pending || !resolve) return;

    setReviewConfirming(true);
    try {
      await deductPathInventoryForLabTests({
        labTestIds: pending.labTestIdsToDeduct,
        patientContextByLabTestId: pending.patientContextByLabTestId,
      });
      pendingDeductRef.current = null;
      reviewResolverRef.current = null;
      setReviewOpen(false);
      resolve({
        ok: true,
        deducted: true,
        checks: pending.checks,
        labTestIdsToDeduct: pending.labTestIdsToDeduct,
      });
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data ||
        error?.message ||
        "Inventory deduction failed.";
      pendingDeductRef.current = null;
      reviewResolverRef.current = null;
      setReviewOpen(false);
      resolve({
        ok: false,
        reason: "deduct_failed",
        checks: pending.checks,
        errorMessage:
          typeof message === "string" ? message : "Inventory deduction failed.",
        error,
      });
    } finally {
      setReviewConfirming(false);
    }
  }, []);

  /**
   * @returns {{
   *   ok: boolean,
   *   deducted?: boolean,
   *   reason?: string,
   *   checks?: array,
   *   errorMessage?: string,
   * }}
   */
  const gateReviewAndDeduct = useCallback(
    async ({
      patientLabTests = [],
      patientContextByLabTestId = {},
      allowMissingExpiry = true,
      catalogLabTests,
    } = {}) => {
      const evaluation = await checkPathBomStockForLabTests(patientLabTests, {
        allowMissingExpiry,
        catalogLabTests,
      });

      if (evaluation.hasBlocking) {
        setStockChecks(evaluation.checks || []);
        setInsufficientOpen(true);
        return {
          ok: false,
          reason: "insufficient_stock",
          checks: evaluation.checks,
        };
      }

      if (
        evaluation.hasBomRequirements &&
        evaluation.labTestIdsToDeduct.length > 0
      ) {
        setStockChecks(evaluation.checks || []);
        pendingDeductRef.current = {
          labTestIdsToDeduct: evaluation.labTestIdsToDeduct,
          patientContextByLabTestId,
          checks: evaluation.checks,
        };

        return await new Promise((resolve) => {
          reviewResolverRef.current = resolve;
          setReviewOpen(true);
        });
      }

      return {
        ok: true,
        deducted: false,
        checks: evaluation.checks || [],
        hasBomRequirements: false,
      };
    },
    []
  );

  const inventoryDialogs = (
    <>
      <PathBomInsufficientStockDialog
        open={insufficientOpen}
        onClose={closeInsufficient}
        checks={stockChecks}
        actionLabel="collect sample"
        title="Cannot collect sample — insufficient consumables"
      />
      <PathBomStockReviewDialog
        open={reviewOpen}
        onClose={cancelReview}
        onConfirm={confirmReview}
        checks={stockChecks}
        confirming={reviewConfirming}
        title="Review pathology consumable stock"
      />
    </>
  );

  return {
    inventoryDialogs,
    gateReviewAndDeduct,
    stockChecks,
  };
}

/**
 * Success alert text after sample collection.
 */
export const buildSampleCollectSuccessMessage = ({
  deducted = false,
  testNames = [],
  sampleIds = [],
} = {}) => {
  const names = (Array.isArray(testNames) ? testNames : []).filter(Boolean);
  const ids = (Array.isArray(sampleIds) ? sampleIds : []).filter(Boolean);
  const namePart = names.length ? ` for: ${names.join(", ")}` : "";
  const idPart = ids.length ? `\nSample ID(s): ${ids.join(", ")}` : "";

  if (deducted) {
    return `Sample collected and inventory deducted successfully${namePart}.${idPart}`;
  }
  return names.length || ids.length
    ? `Samples collected successfully${namePart}.${idPart}`
    : "Samples collected successfully.";
};
